import { Field, TFieldTheme } from '../utility/field/Field'
import { ResultDrop } from '../result-drop/ResultDrop'
import React, {
  ComponentType,
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { IWithDropdownProps, withDropdown } from '../../../lib/hoc/WithDropdown'
import { DropdownPositioner } from '../../../../lib/ui/dropdown-positioner/component/DropdownPositioner'
import { ISearchFieldProps, SearchField } from '../utility/search-field/SearchField'
import { IWithSearchProps, withSearch } from '../../../lib/hoc/WithSearch'
import { IInputProps } from '../utility/input/BaseInput'
import { IIconProps } from '../../../types/IconProps.interface'

export interface IOption {
  text: string
  value: string
  hidden?: boolean
  allowDelete?: boolean
  pinned?: boolean
  allowed_country_ids?: number[]
  icon?: React.ReactNode
  /** ISO-код страны — по нему рисуется флаг (filter/country) */
  code?: string
}

export interface IBpSearchInputProps extends IInputProps, IWithDropdownProps, IWithSearchProps {
  Icon?: ComponentType<IIconProps>
  options?: IOption[]
  label?: string
  action?: JSX.Element
  focus?: boolean
  loading?: boolean
}

const Input = forwardRef((props: IBpSearchInputProps, ref: ForwardedRef<ISearchFieldProps>) => {
  const {
    Icon,
    fieldSize,
    options,
    focus = false,
    open,
    debouncedSearch,
    label,
    action,
    onBlur,
    onFocus,
    loading = false,
    ...inputProps
  } = props

  const [theme, setTheme] = useState<TFieldTheme>('default')
  const [isFocus, setIsFocus] = useState<boolean>(focus)

  const inputRef = useRef<any>(null)
  useImperativeHandle(ref, () => inputRef.current)

  useEffect(() => {
    if (inputProps.disabled) {
      setTheme('disabled')
    } else {
      if (isFocus) {
        setTheme('focus')
      } else {
        setTheme('default')
      }
    }
  })

  const setNativeValue = (element: HTMLInputElement, value = '') => {
    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(element),
      'value',
    )?.set

    if (valueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter?.call(element, value)
    } else {
      valueSetter?.call(element, value)
    }
  }

  const handleSelected = (option: IOption | IOption[] | undefined) => {
    if (!option) return

    if (Array.isArray(option)) {
      setNativeValue(
        inputRef.current,
        option.length ? option[0].text : (inputProps.defaultValue as string),
      )
    } else {
      setNativeValue(inputRef.current, option.text)
    }

    inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
  }

  return (
    <div className='relative'>
      <Field action={action} fieldSize={fieldSize} label={label} theme={theme}>
        <SearchField
          ref={inputRef}
          {...inputProps}
          Icon={Icon}
          fieldSize={fieldSize}
          onBlur={(e) => {
            setIsFocus(false)
            if (onBlur) onBlur(e)
          }}
          onClear={() => {
            setNativeValue(inputRef.current, '')
            inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
          }}
          onFocus={(e) => {
            setIsFocus(true)
            if (onFocus) onFocus(e)
          }}
        />
        {options !== undefined && (
          <DropdownPositioner open={open}>
            <ResultDrop
              asList
              loading={loading}
              options={options}
              pinTop={!!open}
              search={debouncedSearch}
              onSelected={handleSelected}
            ></ResultDrop>
          </DropdownPositioner>
        )}
      </Field>
    </div>
  )
})
export const BpSearchInput = withDropdown(withSearch(Input))
